import React, { useMemo, useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Collapse,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel, Paper,
  Tab,
} from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit, faTextSlash,
} from '@fortawesome/free-solid-svg-icons';
import getBaseEditor, { CommandPanels } from '../../BaseEditor/index';
import TPs from './tp';
import PrintFormEditor from './print/editor';

const MembersTPColumnTypes = {
  member: { type: '/api/household/members/' },
};

function EditorRenderer(getControl, {
  data,
  dataSetter,
  fields,
  currentUser,
  erroredFields,
}) {
  const [tab, setTab] = useState('general');
  const [addrEditMode, setAddrEditMode] = useState(1);
  const currentOrg = useMemo(
    () => (currentUser.is_superuser ? data.org && (data.org.id || null) : currentUser.org.id),
    [currentUser.is_superuser, currentUser.org.id, data.org],
  );

  const localityAreaFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const streetFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const houseFilter = useMemo(
    () => ({ street: data.street && data.street.id }),
    [data.street],
  );

  const orgFilter = useMemo(
    () => ({
      org: currentOrg,
    }),
    [currentOrg],
  );

  return (
    <TabContext value={tab}>
      <TabList onChange={(e, v) => setTab(v)} textColor="primary" indicatorColor="primary">
        <Tab value="general" label="Загальне" />
        <Tab value="household_member" label="Члени домогосподарства" />
        <Tab value="household_realty" label="Нерухомість домогосподарства" />
        <Tab value="household_land" label="Земельні ділянки домогосподарства" />
        <Tab value="household_animals" label="Тварини" />
        <Tab value="household_print" label="Звітність" />
        <Tab value="files" label="Додані файли" />
      </TabList>
      <TabPanel value="general">

        <Grid container spacing={2}>
          {currentUser.is_superuser && (
            <Grid item sm={6}>
              {getControl('org', {
                backend: '/api/catalog/orgs/',
                label: 'ОТГ',
              })}
            </Grid>
          )}

          <Grid item sm={6}>
            {getControl('region', {
              canErase: true,
              backend: '/api/catalog/regions/',
              filter: {
                org: currentUser.is_superuser ? data.org && data.org.id : currentUser.org.id,
              },
            })}
          </Grid>
          <Grid item sm={6}>
            {getControl('payer', {
              backend: '/api/catalog/payers/',
              filter: orgFilter,
            })}

          </Grid>
          <Grid item sm={12}>
            <Collapse in={addrEditMode === 1}>
              <FormControl fullWidth margin="dense">
                <InputLabel htmlFor="addr_input" error={!!erroredFields.address}>{fields.address.label}</InputLabel>
                <Input
                  id="addr_input"
                  value={data.address}
                  onChange={(e) => dataSetter({ address: e.target.value })}
                  error={!!erroredFields.address}
                  margin="dense"
                  endAdornment={(
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle to address classifier"
                        onClick={() => setAddrEditMode(2)}
                      >
                        <FontAwesomeIcon icon={faEdit} size="xs" />
                      </IconButton>
                    </InputAdornment>
              )}
                />
                <FormHelperText error>{erroredFields.address}</FormHelperText>
              </FormControl>
            </Collapse>
            <Collapse in={addrEditMode === 2}>
              <Paper sx={{
                display: 'flex', px: 2, py: 1, gap: 1,
              }}
              >
                {getControl('locality')}
                {getControl('locality_area', { filter: localityAreaFilter })}
                {getControl('street', { filter: streetFilter })}
                {getControl('house', { filter: houseFilter })}
                {getControl('number')}
                <IconButton
                  aria-label="toggle to address classifier"
                  onClick={() => setAddrEditMode(1)}
                  edge="end"
                >
                  <FontAwesomeIcon icon={faTextSlash} size="xs" />
                </IconButton>
              </Paper>
            </Collapse>
          </Grid>
          <Grid item sm={12}>
            {getControl('information')}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="household_member">
        <TPs.MembersTP
          data={data.householdmember_set}
          options={fields.householdmember_set}
          onChange={(e, newRecs) => dataSetter({ householdmember_set: newRecs })}
          overrideColumns={MembersTPColumnTypes}
        />
      </TabPanel>
      <TabPanel value="household_land">
        <TPs.LandsTP
          data={data.householdland_set}
          members={data.householdmember_set}
          payer={data.payer}
          options={fields.householdland_set}
          onChange={(e, newRecs) => dataSetter({ householdland_set: newRecs })}
        />
      </TabPanel>
      <TabPanel value="household_realty" sx={{ overflowX: 'auto' }}>
        <TPs.RealtyTP
          data={data.householdrealty_set}
          options={fields.householdrealty_set}
          onChange={(e, newRecs) => dataSetter({ householdrealty_set: newRecs })}
        />
      </TabPanel>
      <TabPanel value="household_animals">
        <TPs.AnimalsTP
          data={data.householdanimalshistory_set}
          options={fields.householdanimalshistory_set}
          onChange={(e, newRecs) => dataSetter({ householdanimalshistory_set: newRecs })}
        />
      </TabPanel>
      <TabPanel value="household_print">
        <PrintFormEditor
          id={data.id}
        />
      </TabPanel>
    </TabContext>

  );
}

EditorRenderer.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.oneOf([1, 2, 3]),
    locality: PropTypes.shape({
      id: PropTypes.number,
      koatuu: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    street: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    house: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    number: PropTypes.string,
    address: PropTypes.string,
    householdmember_set: PropTypes.arrayOf(PropTypes.shape({})),
    householdrealty_set: PropTypes.arrayOf(PropTypes.shape({})),
    householdland_set: PropTypes.arrayOf(PropTypes.shape({})),
    org: PropTypes.shape({
      id: PropTypes.number,
    }),
    multi_contract: PropTypes.bool,
    contract_property: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/require-default-props
  erroredFields: PropTypes.shape({
    full_name: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.arrayOf(PropTypes.string),
    taxgrouphistory_set: PropTypes.arrayOf(PropTypes.string),
  }),
  fields: PropTypes.shape({
    householdland_set: PropTypes.shape({}),
    householdrealty_set: PropTypes.shape({}),
    householdmember_set: PropTypes.shape({}),
    address: PropTypes.shape({
      label: PropTypes.string,
    }),

  }).isRequired,
};

const CP = CommandPanels.docCommandPanel();
// const CP = CommandPanels.docCommandPanel(PrintForm);
export default getBaseEditor(EditorRenderer, '/api/household/households/', CP);
